import { List, ListItem, Typography } from '@mui/material';
import type { FC, ReactNode } from 'react';
import type { AmsPartUsedType, MediaData } from '../../models';
import { ImageViewer } from '../Dialogs';
import { VisitAmsPart } from '../Faults';
import { AvaniScrollable } from '../Utils';

interface IVAPProps {
  amsPartsUsed?: AmsPartUsedType[];
  keepHeight?: boolean;
}

/**
 * Scrollable gallery for AMS Parts Used
 * @param amsPartsUsed
 * @param keepHeight
 * @constructor
 */
export const AmsPartScrollGallery: FC<IVAPProps> = ({ amsPartsUsed, keepHeight = false }) => {
  return (
    <ScrollableGallery
      items={amsPartsUsed}
      keepHeight={keepHeight}
      emptyStateMsg={'No AMS Parts used'}
      renderItem={(_items, item, _index) => <VisitAmsPart amsPartsUsed={item} />}
    />
  );
};

interface IMediaProps {
  medias?: MediaData[];
  keepHeight?: boolean;
}

/**
 * Scrollable gallery for medias
 * @param medias
 * @param keepHeight
 * @constructor
 */
export const MediasScrollableGallery: FC<IMediaProps> = ({ medias, keepHeight = false }) => {
  return (
    <ScrollableGallery
      items={medias}
      keepHeight={keepHeight}
      emptyStateMsg={'No media available'}
      renderItem={(items, item, index) => (
        <ImageViewer key={index} index={index} imageSrc={items ?? []} avatarWidth={50} avatarHeight={50} />
      )}
    />
  );
};

interface IProps<T> {
  items?: T[];
  emptyStateMsg?: string;
  height?: number;
  keepHeight?: boolean;
  renderItem: (items: T[], item: T, index: number) => ReactNode;
}

/**
 * Generic scrollable gallery component
 * @param items
 * @param height
 * @param keepHeight
 * @param renderItem
 * @constructor
 */
const ScrollableGallery = <T,>({ items, height = 85, keepHeight = false, renderItem, emptyStateMsg }: IProps<T>) => {
  if (!keepHeight && !items?.length) return null;
  return (
    <AvaniScrollable horizontal height={height} sx={{ display: 'grid', alignItems: 'center', paddingBottom: 0 }}>
      <List disablePadding sx={{ width: 'max-content', float: 'left', display: 'block', paddingLeft: 0.5 }}>
        {items?.map((item, index) => (
          <ListItem
            key={index}
            disablePadding
            sx={{
              p: 0,
              display: 'inline-block',
              width: 'fit-content',
              mr: 1
            }}
          >
            {renderItem(items, item, index)}
          </ListItem>
        ))}
      </List>
      {!items?.length && <Typography variant="body1">{emptyStateMsg ?? 'No items available'}</Typography>}
    </AvaniScrollable>
  );
};
